import { MoreVert } from "@mui/icons-material";
import { CircularProgress, IconButton, Popover } from "@mui/material";
import classNames from "classnames";
import * as React from "react";

import { usePopupState } from "@/shared/hooks";
import { type PanelState } from "@/shared/state/visualization";

import { OpenPanelSettings, RemovePanel } from "../controls";

import styles from "./PanelHeader.module.css";
import { PanelIcon } from "./PanelIcon";
import { PanelTitle } from "./PanelTitle";
import { countTools, useCollapseTools } from "./useCollapseTools";

interface PanelHeaderProps {
  backgroundLoading?: boolean;
  additionalTools?: React.ReactNode;
  className?: classNames.Argument;
  state: PanelState;
}

/**
 * A header for a panel that displays the panel title (if any) and controls for managing
 * the panel's layers.
 */
export function PanelHeader(props: PanelHeaderProps) {
  const { additionalTools, className, state, backgroundLoading } = props;

  const tools = [
    additionalTools,
    <OpenPanelSettings key="panel-header-settings" state={state} />,
  ];

  const { shouldCollapse, measuredRow, measuredTools } = useCollapseTools({
    collapseRatio: 0.4,
    // A control appearing or disappearing invalidates the measured width;
    // without that the toolbar could collapse but never come back.
    toolCount: countTools(tools),
  });

  // Only collapse the toolbar into a popup if there are extra toolbar items that make collapsing worthwhile.
  const isCollapsed = shouldCollapse && additionalTools;

  return (
    <div className={classNames(styles.panelHeader, className)}>
      <div
        className={styles.panelRow}
        ref={(node) => node !== null && measuredRow(node)}
      >
        <div className={styles.titleGroup}>
          <PanelIcon className={styles.panelIcon} panelType={state.type} />
          <PanelTitle
            className={styles.title}
            title={state.title ?? null}
            endAdornment={
              backgroundLoading && (
                <IconButton disabled sx={{ padding: 0 }}>
                  <CircularProgress size={"0.85rem"} />
                </IconButton>
              )
            }
          />
        </div>
        <div className={styles.panelControls} data-not-draggable="true">
          {isCollapsed ? (
            <HeaderOverflow>
              {/*
                Deliberately unmeasured: the collapsed toolbar's width would
                read as "fits" and cause an expand/collapse oscillation.
                Un-collapsing relies on `useCollapseTools` discarding the
                measured width when the control count changes.
              */}
              {...tools}
            </HeaderOverflow>
          ) : (
            <div
              className={styles.panelControls}
              data-not-draggable="true"
              ref={(node) => node !== null && measuredTools(node)}
            >
              {...tools}
            </div>
          )}
          <RemovePanel panelId={state.id} data-not-draggable="true" />
        </div>
      </div>
    </div>
  );
}

function HeaderOverflow({ children }: React.PropsWithChildren) {
  const { isOpen, anchor, open, close } = usePopupState();

  return (
    <>
      <IconButton
        aria-label="show-panel-options"
        onClick={(event) => open({ anchorEl: event.currentTarget })}
        size="small"
        title="Show all options."
      >
        <MoreVert fontSize="small"></MoreVert>
      </IconButton>
      <Popover
        anchorEl={anchor}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          root: {
            // @ts-expect-error ts(2353): data-not-draggable is a custom prop to disable drag interactions.
            "data-not-draggable": true,
          },
        }}
        open={isOpen}
        onClose={close}
      >
        <div className={styles.popover}>{children}</div>
      </Popover>
    </>
  );
}
